import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Checkbox, CircularProgress, Modal, Snackbar, Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FiSearch } from 'react-icons/fi';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryCreate from './CategoryCreate'; // Assuming this component exists
import { motion } from 'framer-motion'; // Added for animations
import CategoryEdit from './CategoryEdit';
import { BASE_URL } from '../../utils/endpoints';

const truncateText = (text, maxLength = 70) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
};

const CategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleOpen = (category) => {
    setSelectedCategory(category);
    setOpenEdit(true);
  };

  const handleClose = () => {
    setOpenEdit(false);
    setSelectedCategory(null);
  };

  const handleOpenCreate = () => {
    setOpenCreate(true);
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleConfirmDeleteOpen = () => {
    if (selectedRows.length > 0) {
      setConfirmDelete(true);
    } else {
      setSnackbarMessage('Please select at least one category to delete');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
    }
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDelete(false);
  };

  const handleBulkDelete = async () => {
    try {
      const deletePromises = selectedRows.map(id =>
        fetch(`${BASE_URL}/categories/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        }).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to delete category with ID ${id}`);
          }
          return response;
        })
      );
      const responses = await Promise.all(deletePromises);
      const allSuccessful = responses.every(response => response.ok);

      if (allSuccessful) {
        setCategories(categories.filter(category => !selectedRows.includes(category.id)));
        setSelectedRows([]);
        setSnackbarMessage(`Successfully deleted ${selectedRows.length} category(s)`);
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage('Failed to delete some categories');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      console.error('Error deleting categories:', error);
      setSnackbarMessage('Error deleting categories: ' + error.message);
      setSnackbarSeverity('error');
    } finally {
      setOpenSnackbar(true);
      setConfirmDelete(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      // Fetch all categories (remove pagination parameters)
      const response = await fetch(`${BASE_URL}/categories`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();

      const formattedCategories = (data.categories || []).map(category => ({
        id: category._id || `fallback-${Math.random()}`,
        name: category.name || 'Unnamed Category',
      }));

      setCategories(formattedCategories);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCategoryUpdated = () => {
    fetchCategories();
  };

  const handleCategoryCreated = () => {
    fetchCategories();
    handleCloseCreate();
  };

  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.includes(params.row.id)}
          onChange={(event) => {
            setSelectedRows(
              event.target.checked
                ? [...selectedRows, params.row.id]
                : selectedRows.filter(id => id !== params.row.id)
            );
          }}
          sx={{
            color: '#2e7d32',
            '&.Mui-checked': {
              color: '#2e7d32',
            },
          }}
        />
      ),
    },
    {
      field: 'name',
      headerName: <strong style={{ color: "#2e7d32" }}>Category</strong>,
      flex: 1,
      renderCell: (params) => (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            p: 1,
            height: '100%',
            width: '100%'
          }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, cursor: 'pointer', color: '#2e7d32', fontSize: '16px', textDecoration: 'none' }}
                onClick={() => handleOpen(params.row)}
              >
                {truncateText(params.row.name || 'Unnamed Product')}
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      ),
    },
  ];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mt: 8 }}>
          <Typography color="error">Error: {error}</Typography>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ mt: 2, borderRadius: 2, p: 3, bgcolor: '#fff', boxShadow: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
            Categories
          </Typography>
          <Typography variant="body2" color="#2e7d32" sx={{ mb: 2 }}>
            Dashboard • Categories • List
          </Typography>
        </motion.div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Search..."
                size="small"
                InputProps={{
                  startAdornment: <FiSearch style={{ marginRight: 8, color: '#9ca3af' }} />,
                }}
              />
            </Box>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  borderColor: '#9ca3af',
                  color: '#9ca3af',
                  '&:hover': { borderColor: 'tomato', color: 'red' },
                }}
                startIcon={<DeleteIcon />}
                onClick={handleConfirmDeleteOpen}
                disabled={selectedRows.length === 0}
              >
                Delete Selected ({selectedRows.length})
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  backgroundColor: '#2e7d32',
                  '&:hover': { backgroundColor: '#45a049' },
                }}
                onClick={handleOpenCreate}
              >
                Add Category
              </Button>
            </Box>
          </motion.div>
        </Box>
        <DataGrid
          rows={categories}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight
          rowHeight={60}
          getRowId={(row) => row.id}
          pagination
          sx={{
            borderRadius: 2,
            '& .MuiDataGrid-columnHeaders': { bgcolor: '#f9fafb' },
          }}
        />

        <CategoryEdit
          open={openEdit}
          handleClose={handleClose}
          category={selectedCategory}
          onCategoryUpdated={handleCategoryUpdated}
        />

        <Modal
          open={openCreate}
          onClose={handleCloseCreate}
          aria-labelledby="create-category-modal-title"
          aria-describedby="create-category-modal-description"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 650,
                maxHeight: '80vh',
                overflowY: 'auto',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography
                id="create-category-modal-title"
                variant="h6"
                component="h2"
                sx={{ color: '#2e7d32', fontWeight: 'bold' }}
              >
                Create New Category
              </Typography>
              <CategoryCreate
                onCategoryCreated={handleCategoryCreated}
                handleClose={handleCloseCreate}
              />
            </Box>
          </motion.div>
        </Modal>

        <Modal
          open={confirmDelete}
          onClose={handleConfirmDeleteClose}
          aria-labelledby="confirm-delete-modal-title"
          aria-describedby="confirm-delete-modal-description"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography id="confirm-delete-modal-title" variant="h6" component="h2" sx={{ color: 'red' }}>
                Confirm Deletion
              </Typography>
              <Typography id="confirm-delete-modal-description" variant="body1">
                Are you sure you want to delete {selectedRows.length} selected category(s)?
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleConfirmDeleteClose}
                  sx={{ color: '#9ca3af', borderColor: '#9ca3af' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleBulkDelete}
                  sx={{ backgroundColor: 'red', '&:hover': { backgroundColor: 'darkred' } }}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Modal>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </motion.div>
  );
};

export default CategoriesList;